import React, {useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import bluredNavbar from "../../../assets/svg/navigation.svg";
import GrayModal from "../../../ui-kit/src/dataDisplay/GrayModal";
import {noop} from "../../../common/utilities";
import LoginMutation from "../../../mutations/LoginMutation";
import {setAuthToken} from "../../../Environment";
import {
  acceptInvite,
  fetchPublicInvite,
  getErrorMessage,
  getErrorSubstatus,
  PublicInvite,
} from "../../Users/Members/membersApi";
import styles from "./AcceptInvite.module.scss";

type LoadStatus = "loading" | "ready" | "not_found" | "error";
type SubmitStatus = "idle" | "submitting" | "signing_in" | "done";

// This app is for brands. People invited to a retailer sign in on the retailers app.
const RETAILERS_LOGIN_URL = "https://retailers.vuuh.com/login";
const BRANDS_LOGIN_URL = "/login";

// These rules are the same as the backend rules.
const PASSWORD_RULES: { id: string, label: string, test: (value: string) => boolean }[] = [
  {id: "length", label: "At least 8 characters", test: value => value.length >= 8},
  {id: "digit", label: "A number", test: value => /\d/.test(value)},
  {id: "special", label: "A special character", test: value => /[^a-zA-Z0-9]/.test(value)},
];

function getEntityInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
  return initials ? initials : "V";
}

export default function AcceptInvite() {
  const {key} = useParams<{ key: string }>();
  const history = useHistory();

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const requestRef = useRef(0);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invite, setInvite] = useState<PublicInvite | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSubstatus, setSubmitSubstatus] = useState<string | null>(null);

  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setLoadStatus("loading");
    setLoadError(null);
    fetchPublicInvite(key)
      .then(response => {
        if (!mountedRef.current || requestId !== requestRef.current) return;
        setInvite(response.item);
        setLogoFailed(false);
        setLoadStatus("ready");
      })
      .catch(error => {
        if (!mountedRef.current || requestId !== requestRef.current) return;
        if (getErrorSubstatus(error) === "not_found") {
          setLoadStatus("not_found");
          return;
        }
        setLoadStatus("error");
        setLoadError(getErrorMessage(error));
      });
  }, [key, reloadKey]);

  useEffect(() => {
    if (loadStatus === "ready" && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [loadStatus]);

  const passedRules = PASSWORD_RULES.filter(rule => rule.test(password));
  const isPasswordValid = passedRules.length === PASSWORD_RULES.length;
  const isBusy = submitStatus === "submitting" || submitStatus === "signing_in";

  const signInAfterAccept = (email: string) => {
    setSubmitStatus("signing_in");
    LoginMutation(
      email, password,
      response => {
        if (!mountedRef.current) return;
        setAuthToken(response.tokenAuth.token);
        history.push("/");
      },
      () => {
        // The account exists. Let the person sign in manually.
        if (!mountedRef.current) return;
        setSubmitStatus("done");
      });
  };

  const submit = () => {
    if (isBusy || !invite) {
      return;
    }
    setPasswordTouched(true);
    if (!isPasswordValid) {
      if (passwordInputRef.current) passwordInputRef.current.focus();
      return;
    }
    requestRef.current += 1;
    const requestId = requestRef.current;
    setSubmitStatus("submitting");
    setSubmitError(null);
    setSubmitSubstatus(null);
    acceptInvite(key, password)
      .then(response => {
        if (!mountedRef.current || requestId !== requestRef.current) return;
        // The LoginMutation of this app uses the brands scope.
        if (invite.type === "brand") {
          signInAfterAccept(response.item.email);
        } else {
          setSubmitStatus("done");
        }
      })
      .catch(error => {
        if (!mountedRef.current || requestId !== requestRef.current) return;
        const substatus = getErrorSubstatus(error);
        setSubmitStatus("idle");
        if (substatus === "not_found") {
          setLoadStatus("not_found");
          return;
        }
        setSubmitSubstatus(substatus);
        setSubmitError(getErrorMessage(error));
      });
  };

  const loginUrl = invite && invite.type === "retailer" ? RETAILERS_LOGIN_URL : BRANDS_LOGIN_URL;

  const renderLogo = (entity: PublicInvite["entity"]) => {
    if (entity.logo && !logoFailed) {
      return <img src={entity.logo}
                  alt=""
                  className={styles.logoImage}
                  onError={() => setLogoFailed(true)}/>;
    }
    return <span className={styles.logoInitials}>{getEntityInitials(entity.name)}</span>;
  };

  const renderLoading = () => <div className={styles.content} aria-busy="true">
    <span className="sr-only">Loading your invite</span>
    <div className={styles.hero} aria-hidden="true">
      <span className={`${styles.skeleton} ${styles.skeletonLogo}`}/>
      <span className={`${styles.skeleton} ${styles.skeletonEyebrow}`}/>
      <span className={`${styles.skeleton} ${styles.skeletonTitle}`}/>
      <span className={`${styles.skeleton} ${styles.skeletonLine}`}/>
    </div>
    <div className={styles.form} aria-hidden="true">
      <span className={`${styles.skeleton} ${styles.skeletonField}`}/>
      <span className={`${styles.skeleton} ${styles.skeletonField}`}/>
      <span className={`${styles.skeleton} ${styles.skeletonButton}`}/>
    </div>
  </div>;

  const renderNotFound = () => <div className={`${styles.content} ${styles.stateContent}`}>
    <span className={styles.stateIcon} aria-hidden="true"><i className="fa-regular fa-link-slash"/></span>
    <h1 className={styles.stateTitle}>This invite link no longer works</h1>
    <p className={styles.stateText}>
      Someone used or cancelled this invite. If you created your account, sign in. If not, ask the person
      who invited you to send a new invite.
    </p>
    <a href="/login" className={styles.primaryButton}>
      <i className="fa-regular fa-arrow-right-to-bracket" aria-hidden="true"/>
      Go to sign in
    </a>
  </div>;

  const renderError = () => <div className={`${styles.content} ${styles.stateContent}`}>
    <div className={styles.errorBox} role="alert">
      <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>
      <div>
        <strong>Your invite cannot be loaded</strong>
        <span>{loadError}</span>
      </div>
      <button type="button" className={styles.linkButton} onClick={() => setReloadKey(current => current + 1)}>
        Try again
      </button>
    </div>
  </div>;

  const renderDone = (current: PublicInvite) => <div className={`${styles.content} ${styles.stateContent}`}>
    <span className={`${styles.stateIcon} ${styles.stateIconSuccess}`} aria-hidden="true"><i className="fa-regular fa-check"/></span>
    <h1 className={styles.stateTitle}>Welcome to {current.entity.name}</h1>
    <p className={styles.stateText}>
      Your Vuuh account is ready. Sign in with <span className={styles.emailChip}>{current.email}</span> and the
      password that you chose.
    </p>
    <a href={loginUrl} className={styles.primaryButton}>
      <i className="fa-regular fa-arrow-right-to-bracket" aria-hidden="true"/>
      Sign in
    </a>
  </div>;

  const renderForm = (current: PublicInvite) => {
    const visiblePasswordError = passwordTouched && !isPasswordValid;
    const firstName = current.name ? current.name.trim().split(" ")[0] : "";
    let submitLabel = `Create account and join ${current.entity.name}`;
    if (submitStatus === "submitting") {
      submitLabel = "Creating your account…";
    } else if (submitStatus === "signing_in") {
      submitLabel = "Signing you in…";
    }
    return <div className={styles.content}>
      <div className={styles.hero}>
        <span className={styles.logo} aria-hidden="true">{renderLogo(current.entity)}</span>
        <span className={styles.eyebrow}>You're invited</span>
        <h1 className={styles.title}>Create your Vuuh account and join {current.entity.name}</h1>
        <p className={styles.subtitle}>
          {firstName ? `Hi ${firstName}, you` : "You"} are invited to join as
          {" "}<span className={styles.rolePill}>{current.role}</span>. Choose a password to start.
        </p>
      </div>

      <form className={styles.form}
            noValidate
            onSubmit={event => {
              event.preventDefault();
              submit();
            }}>
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Email</span>
            <span className={styles.summaryValue} title={current.email}>{current.email}</span>
          </div>
          {current.name && <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Name</span>
            <span className={styles.summaryValue} title={current.name}>{current.name}</span>
          </div>}
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Company</span>
            <span className={styles.summaryValue} title={current.entity.name}>{current.entity.name}</span>
          </div>
        </div>

        {/* Lets password managers save the password for the correct account. */}
        <input type="email" name="email" value={current.email} autoComplete="username" readOnly hidden/>

        <div className={styles.field}>
          <label htmlFor="accept-invite-password" className={styles.label}>Password</label>
          <div className={`${styles.inputWrap} ${visiblePasswordError ? styles.inputWrapError : ""}`}>
            <i className={`fa-regular fa-lock ${styles.inputIcon}`} aria-hidden="true"/>
            <input id="accept-invite-password"
                   ref={passwordInputRef}
                   className={styles.input}
                   type={showPassword ? "text" : "password"}
                   value={password}
                   autoComplete="new-password"
                   placeholder="Choose a password"
                   disabled={isBusy}
                   aria-invalid={visiblePasswordError ? true : undefined}
                   aria-describedby="accept-invite-password-rules"
                   onChange={event => {
                     setPassword(event.target.value);
                     setSubmitError(null);
                     setSubmitSubstatus(null);
                   }}
                   onBlur={() => {
                     if (password) setPasswordTouched(true);
                   }}/>
            <button type="button"
                    className={styles.revealButton}
                    onClick={() => setShowPassword(value => !value)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    disabled={isBusy}>
              <i className={`fa-regular ${showPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true"/>
            </button>
          </div>
          <ul className={styles.rules} id="accept-invite-password-rules" aria-label="Password requirements">
            {PASSWORD_RULES.map(rule => {
              const passed = rule.test(password);
              const missedClass = visiblePasswordError && !passed ? styles.ruleMissed : "";
              return <li key={rule.id} className={passed ? styles.ruleMet : missedClass}>
                <i className={`fa-regular ${passed ? "fa-circle-check" : "fa-circle"}`} aria-hidden="true"/>
                {rule.label}
                <span className="sr-only">{passed ? " (done)" : " (missing)"}</span>
              </li>;
            })}
          </ul>
        </div>

        {submitError && <div className={styles.errorBox} role="alert">
          <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>
          <div>
            <strong>Your account is not created</strong>
            <span>{submitError}</span>
          </div>
          {submitSubstatus === "email_taken" && <a href={loginUrl} className={styles.linkButton}>Sign in</a>}
        </div>}

        <button type="submit"
                className={`${styles.primaryButton} ${styles.submitButton}`}
                disabled={isBusy || !password}
                title={!password ? "Choose a password first" : undefined}>
          {isBusy
            ? <span className={styles.spinner} aria-hidden="true"/>
            : <i className="fa-regular fa-user-check" aria-hidden="true"/>}
          {submitLabel}
        </button>

        <p className={styles.footnote}>
          Already have a Vuuh account? <a href={loginUrl}>Sign in</a>
        </p>
      </form>
    </div>;
  };

  let content: JSX.Element;
  if (loadStatus === "loading") {
    content = renderLoading();
  } else if (loadStatus === "not_found") {
    content = renderNotFound();
  } else if (loadStatus === "error" || !invite) {
    content = renderError();
  } else if (submitStatus === "done") {
    content = renderDone(invite);
  } else {
    content = renderForm(invite);
  }

  return <div className="app flex-row align-items-center">
    <img src={bluredNavbar} className="blured-navbar" alt="Blurred Vuuh navbar"/>
    <GrayModal isOpen={true}
               toggle={noop}
               backdrop="static"
               className={styles.modal}
               bodyContent={content}/>
  </div>;
}
